// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';

import { ApiError } from '../api/client';
import type { Device as DeviceRecord, PortsResult } from '../api/devices';
import { devicesApi } from '../api/devices';
import { SshTrust } from '../components/device/SshTrust';
import { PortControl } from '../components/switch/PortControl';
import { DeviceLogs } from '../components/observability/DeviceLogs';

/**
 * One managed switch.
 *
 * # THIS PAGE IS MEANT TO FEEL LIKE THE HARDWARE
 *
 * A header that says whether the thing is reachable and trusted, a picture of
 * its ports, and a table underneath. An operator opening it should recognise
 * their switch, not a database record.
 *
 * # THE TABS ARE NOT DECORATION
 *
 * There is exactly one, because exactly one thing works. VLANs, PoE and
 * neighbours arrive as tabs when they arrive as capabilities — a row of empty
 * pages would suggest Meshvara can do things it cannot, and the first time an
 * operator clicks one and finds nothing is the last time they trust the rest.
 *
 * # CAPABILITY DECIDES WHAT IS OFFERED
 *
 * `set_port_enabled` in the driver's operation list is what puts a usable
 * control in the drawer. Meshvara knowing the operation exists is not the same
 * as this device being able to perform it.
 */
export function Device() {
  const { deviceId = '' } = useParams();

  const [device, setDevice] = useState<DeviceRecord | null>(null);
  const [ports, setPorts] = useState<PortsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [reading, setReading] = useState(false);
  const [probing, setProbing] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const loadDevice = useCallback(async () => {
    const record = await devicesApi.get(deviceId);
    setDevice(record);
    return record;
  }, [deviceId]);

  const readPorts = useCallback(async () => {
    setReading(true);
    try {
      setPorts(await devicesApi.ports(deviceId));
      setError(null);
    } catch (err) {
      // A read that cannot even be attempted — no Gateway, no trusted key — is
      // reported as itself rather than as an empty switch.
      setError(err instanceof ApiError ? err : null);
      setPorts(null);
    } finally {
      setReading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loadDevice();
        if (cancelled) return;
        // ONLY READ PORTS WHEN THERE IS A TRUSTED IDENTITY. Asking otherwise
        // produces a refusal the operator did not ask for, on a page whose first
        // job is to tell them the identity needs confirming.
        // NO AUTOMATIC ALL-PORTS READ. It fails on this firmware, and an error
        // that appears on every page load is an error nobody reads.
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err : null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadDevice, readPorts]);

  async function probe() {
    setProbing(true);
    try {
      const result = await devicesApi.probe(deviceId);
      setDevice(result.device);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setProbing(false);
    }
  }

  if (loading) {
    return (
      <Center h={280}>
        <Loader />
      </Center>
    );
  }

  if (!device) {
    return (
      <Alert color="red" title="Device not found">
        <Anchor component={Link} to="/devices">
          Back to devices
        </Anchor>
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Anchor component={Link} to="/devices" size="sm">
          ← Devices
        </Anchor>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={2}>
            <Title order={2}>{device.name}</Title>
            <Text size="sm" c="dimmed">
              {device.driver_id} · {device.management_address}:{device.management_port}
            </Text>
          </Stack>
          <Group gap="xs">
            {/* Transport is shown for diagnostics. It is never how an action is
                described: an operator disables a port, they do not send SSH. */}
            <Badge variant="default">Management: {device.transport.toUpperCase()}</Badge>
            {device.last_seen_at ? (
              <Badge variant="light" color="teal">
                Last contact {new Date(device.last_seen_at).toLocaleString()}
              </Badge>
            ) : (
              <Badge variant="light" color="gray">
                Never contacted
              </Badge>
            )}
          </Group>
        </Group>
      </Stack>

      <SshTrust
        device={device}
        canModify
        probing={probing}
        onProbe={probe}
        onTrust={async (fingerprint) => {
          setDevice(await devicesApi.trust(deviceId, fingerprint));
          await readPorts();
        }}
        onReplace={async (fingerprint) => {
          setDevice(await devicesApi.replaceTrust(deviceId, fingerprint));
          await readPorts();
        }}
      />

      {error ? (
        <Alert color="orange" variant="light" title="Could not read this device">
          {error.problem.detail ?? error.message}
        </Alert>
      ) : null}

      {device.ssh_trust_state === 'trusted' ? (
        <Tabs defaultValue="ports">
          <Tabs.List>
            <Tabs.Tab value="ports">Ports</Tabs.Tab>
            <Tabs.Tab value="logs">Logs</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="ports" pt="md">
            <Stack gap="md">
              {/* THE MINIMAL CONTROL COMES FIRST, deliberately.
                  It reads ONE port through the path that has been proven, and
                  is the thing the physical acceptance uses. The all-ports view
                  below it depends on a command whose output nobody has captured
                  from this model. */}
              <PortControl deviceId={device.id} />

              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {ports?.ports.length
                    ? `${ports.ports.length} ports reported by the device`
                    : 'No ports read yet'}
                </Text>
                <Button size="xs" variant="default" loading={reading} onClick={readPorts}>
                  Refresh
                </Button>
              </Group>

              {/* THE ALL-PORTS VIEW IS NOT HERE YET, AND THE REASON IS REAL.
                  `show interfaces status` does not produce a table this driver
                  can read on GWN7806P firmware — measured against the physical
                  switch, not assumed. Rendering a panel that errors on every
                  page load underneath a control that works would train an
                  operator to ignore errors, which is worse than the gap. */}
              <Alert variant="light" title="Listing every port is not supported on this model yet">
                This firmware&apos;s all-ports command does not return a table
                Meshvara can read. Use the port control above, which reads one
                port with a command confirmed against this switch.
              </Alert>
            </Stack>
          </Tabs.Panel>
          {/* WHAT THIS DEVICE HAS ACTUALLY DONE.
              The tab an operator opens when a port change did not do what they
              expected — the commands sent, what the switch said back, and where
              the time went. */}
          <Tabs.Panel value="logs" pt="md">
            <DeviceLogs deviceId={device.id} />
          </Tabs.Panel>
        </Tabs>
      ) : null}

    </Stack>
  );
}
